import * as Yup from "yup";
import { useFormik } from "formik";
import { theme } from "../../theme/theme";
import { V3Button } from "../Button";
import { V3TextInput, V3TextArea } from "../TextInput/V3TextInput";
import { V3ModalBase } from "../V3ModalBase";
import { VerticalSpacing } from "../VerticalSpacing";
import { RightAlignPanel } from "../ReusableStyledComponents/RightAlignPanel";
import { Collection, Visibility } from "@authory-packages/core";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";

export type CreateCollectionHandler = (data: CreateUpdateCollectionFormik) => Promise<Collection | undefined>;

export interface CreateUpdateCollectionFormik {
    name?: string;
    description?: string;
    visibility?: Visibility;
    order?: number;
}

interface CreateUpdateCollectionModalProps {
    onSubmit: CreateCollectionHandler;
    setModalOpen: (open: boolean) => void;
    modalOpen: boolean;
    title: string;
    name?: string;
    description?: string;
    btnLabel?: string;
    modalDescription?: string;
    hideDescription?: boolean;
    namePlaceholder?: string;
}

export const CreateUpdateCollectionModal = ({
    onSubmit,
    setModalOpen,
    modalOpen,
    title,
    name,
    description,
    modalDescription,
    hideDescription = false,
    namePlaceholder = "e.g. My Top 10",
    btnLabel = "Add"
}: CreateUpdateCollectionModalProps) => {
    const ccformik = useFormik<CreateUpdateCollectionFormik>({
        enableReinitialize: true,
        initialValues: {
            name: name || "",
            description: description || ""
        },
        onSubmit: (data, formikHelpers) => {
            setModalOpen(false);
            onSubmit(data);
            formikHelpers.resetForm();
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required("Please insert a name")
        })
    });

    return (
        <V3ModalBase
            isOpen={modalOpen}
            onClose={() => {
                setModalOpen(false);
                ccformik.resetForm();
            }}
            title={title}
            bottomPanel={
                <RightAlignPanel>
                    <V3Button
                        text={btnLabel}
                        autoWidth
                        onClick={() => {
                            ccformik.submitForm();
                        }}
                    />
                </RightAlignPanel>
            }
        >
            {!!modalDescription && (
                <VerticalSpacing bottom={36}>
                    <V3BodyGrey48>{modalDescription}</V3BodyGrey48>
                </VerticalSpacing>
            )}
            <form onSubmit={ccformik.handleSubmit}>
                <VerticalSpacing bottom={hideDescription ? 0 : theme.v3.spacings.spacing6}>
                    <V3TextInput
                        label="Name"
                        name="name"
                        placeholder={namePlaceholder}
                        onChange={ccformik.handleChange}
                        onBlur={ccformik.handleBlur}
                        value={ccformik.values.name}
                        error={ccformik.errors.hasOwnProperty("name") && ccformik.touched.hasOwnProperty("name") ? ccformik.errors.name : undefined}
                    />
                </VerticalSpacing>
                {!hideDescription && (
                    <V3TextArea
                        name="description"
                        onChange={ccformik.handleChange}
                        onBlur={ccformik.handleBlur}
                        value={ccformik.values.description}
                        label="Description (optional)"
                        placeholder="e.g. Here are my best pieces about Tesla!"
                    />
                )}
            </form>
        </V3ModalBase>
    );
};
